import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Input,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  VStack,
  HStack,
  Spinner,
  Badge,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { SearchIcon, CloseIcon, CheckIcon } from "@chakra-ui/icons";
import useApi from "hooks/useApi";
import { AGENT_PROJECT_MAPPING, AGENTS_LISTING_API } from "constants/endpoints";
import { showToast } from "components/toast/Toast";

const AddAgentsToProjectModal = ({ isOpen, onClose, project }) => {
  const { apiCall } = useApi();
  const userInputRef = useRef(null);
  const menuRef = useRef(null);
  const debounceTimer = useRef(null);

  // State Management
  const [searchQuery, setSearchQuery] = useState("");
  const [allAgents, setAllAgents] = useState([]);
  const [assignedAgents, setAssignedAgents] = useState([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState(new Set());
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Fetch existing assigned agents
  const fetchAssignedAgents = useCallback(async () => {
    if (!project?._id) return;
    setIsLoadingInitial(true);
    try {
      const response = await apiCall.get(AGENT_PROJECT_MAPPING, {
        project_id: project._id,
      });
      const agents = response?.data?.agents || [];
      setAssignedAgents(agents);
      const assignedIds = new Set(
        agents.map((a) => a.agent_id?.toString() || a._id?.toString())
      );
      setSelectedAgentIds(assignedIds);
    } catch (error) {
      console.error("Error fetching assigned agents:", error);
      showToast({
        message: "Failed to load assigned agents",
        success: false,
        customStyle: {
          background: "#F24052",
          color: "#fff",
          padding: "10px",
        },
      });
    } finally {
      setIsLoadingInitial(false);
    }
  }, [project?._id]);

  // Fetch all agents on component mount
  useEffect(() => {
    if (isOpen) {
      fetchAssignedAgents();
      if (!hasSearched) {
        fetchAgents("");
      }
    }
  }, [isOpen, hasSearched]);

  // Fetch agents based on search query
  const fetchAgents = useCallback(async (query = "") => {
    if (!query.trim()) return setAllAgents([]);
    setIsLoadingSearch(true);
    try {
      const payload = {
        search_text: query,
        status: "active",
      };
      const response = await apiCall.get(AGENTS_LISTING_API, payload);
      const agents = response?.data?.data || [];
      setAllAgents(agents);
      setHasSearched(true);
    } catch (error) {
      console.error("Error fetching agents:", error);
      setAllAgents([]);
    } finally {
      setIsLoadingSearch(false);
    }
  }, []);

  // Handle search input with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsMenuOpen(true);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchAgents(value);
    }, 600);
  };

  // Handle agent selection/deselection
  const toggleAgentSelection = (agentId) => {
    const newSelected = new Set(selectedAgentIds);
    if (newSelected.has(agentId)) {
      newSelected.delete(agentId);
    } else {
      newSelected.add(agentId);
    }
    setSelectedAgentIds(newSelected);
  };

  // Get agent details for display
  const getAgentDisplay = (agent) => {
    const fullName =
      agent.user?.full_name ||
      `${agent.user?.first_name || ""} ${agent.user?.last_name || ""}`.trim() ||
      "Unknown User";
    const phone = agent.user?.phone || "N/A";
    return { fullName, phone };
  };

  // Get unassigned agents (for dropdown)
  const unassignedAgents = allAgents.filter(
    (agent) => !selectedAgentIds.has(agent._id?.toString())
  );

  // Get currently assigned agents
  const currentlyAssigned = Array.from(selectedAgentIds)
    .map((id) => {
      const agentInList = allAgents.find((a) => a._id?.toString() === id);
      if (agentInList) return agentInList;

      const agentInAssigned = assignedAgents.find(
        (a) =>
          a.agent_id?._id?.toString() === id || a.agent_id?.toString() === id
      );
      if (agentInAssigned) {
        return {
          _id: agentInAssigned.agent_id?._id || agentInAssigned.agent_id,
          user: agentInAssigned.user_id || agentInAssigned.agent_id?.user_id,
        };
      }
      return null;
    })
    .filter(Boolean);

  // Handle submit
  const handleSubmit = async () => {
    if (!project?._id) {
      showToast({
        message: "Project information is missing",
        success: false,
        customStyle: {
          background: "#F24052",
          color: "#fff",
          padding: "10px",
        },
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiCall.post(AGENT_PROJECT_MAPPING, {
        project_id: project._id,
        agent_ids: Array.from(selectedAgentIds),
      });

      if (response?.statusCode === 200 || response?.status === 200) {
        showToast({
          message: response?.data?.message || "Agents assigned successfully",
          success: true,
          customStyle: {
            background: "#52c41a",
            color: "#fff",
            padding: "10px",
          },
        });
        setSearchQuery("");
        onClose();
      }
    } catch (error) {
      console.error("Error assigning agents:", error);
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to assign agents";

      showToast({
        message: `Error: ${errorMessage}`,
        success: false,
        customStyle: {
          background: "#F24052",
          color: "#fff",
          padding: "10px",
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ md: "2xl" }}>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent
        borderRadius={{ base: "0", md: "lg" }}
        maxH={{ base: "100vh", md: "auto" }}
        display="flex"
        flexDirection="column"
        width={"60vw"}
      >
        <ModalHeader
          bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          color="white"
          borderTopRadius={{ base: "0", md: "lg" }}
          py={6}
        >
          <HStack justify="space-between" align="center">
            <VStack align="start" spacing={1}>
              <Text fontSize="xl" fontWeight="bold">
                Assign Agents
              </Text>
              <Text fontSize="sm" opacity={0.9}>
                {project?.name || "Project"}
              </Text>
            </VStack>
            <ModalCloseButton color="white" position="relative" />
          </HStack>
        </ModalHeader>

        <ModalBody
          flex="1"
          overflowY="auto"
          py={6}
          px={{ base: 4, md: 6 }}
          bg="gray.50"
        >
          {isLoadingInitial ? (
            <VStack justify="center" align="center" minH="300px" spacing={4}>
              <Spinner size="lg" color="purple.500" />
              <Text color="gray.500">Loading agents...</Text>
            </VStack>
          ) : (
            <VStack spacing={6} align="stretch">
              {/* Search Section */}
              <Box ref={menuRef} position="relative">
                <InputGroup size="lg">
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    ref={userInputRef}
                    placeholder="Search agents by name, email, or phone..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => setIsMenuOpen(true)}
                    borderColor="gray.300"
                    _focus={{
                      borderColor: "purple.500",
                      boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.1)",
                    }}
                    bg="white"
                    borderRadius="lg"
                  />
                </InputGroup>

                {/* Dropdown Menu */}
                {isMenuOpen && searchQuery && (
                  <Box
                    position="absolute"
                    width="100%"
                    zIndex={1000}
                    bg="white"
                    boxShadow="lg"
                    borderRadius="lg"
                    mt={2}
                    border="1px"
                    borderColor="gray.200"
                    maxH="300px"
                    overflowY="auto"
                  >
                    {isLoadingSearch ? (
                      <VStack py={4} justify="center" spacing={2}>
                        <Spinner size="sm" color="purple.500" />
                        <Text fontSize="sm" color="gray.500">
                          Searching...
                        </Text>
                      </VStack>
                    ) : unassignedAgents.length > 0 ? (
                      unassignedAgents.map((agent) => {
                        const { fullName, phone } = getAgentDisplay(agent);
                        const isSelected = selectedAgentIds.has(
                          agent._id?.toString()
                        );
                        return (
                          <Box
                            key={agent._id}
                            p={3}
                            borderBottom="1px"
                            borderColor="gray.100"
                            _last={{ borderBottom: "none" }}
                            cursor="pointer"
                            _hover={{ bg: "purple.50" }}
                            onClick={() =>
                              toggleAgentSelection(agent._id?.toString())
                            }
                            transition="all 0.2s"
                          >
                            <HStack justify="space-between" spacing={3}>
                              <VStack align="start" spacing={0} flex={1}>
                                <Text
                                  fontWeight="600"
                                  fontSize="sm"
                                  color="gray.800"
                                >
                                  {fullName}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {phone}
                                </Text>
                              </VStack>
                              <Box
                                w={5}
                                h={5}
                                borderRadius="md"
                                border="2px"
                                borderColor={
                                  isSelected ? "purple.500" : "gray.300"
                                }
                                bg={isSelected ? "purple.500" : "transparent"}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                transition="all 0.2s"
                              >
                                {isSelected && (
                                  <CheckIcon color="white" w={3} h={3} />
                                )}
                              </Box>
                            </HStack>
                          </Box>
                        );
                      })
                    ) : (
                      <Box py={6} textAlign="center">
                        <Text color="gray.500" fontSize="sm">
                          {allAgents.length === 0
                            ? "No agents found"
                            : "All agents are already assigned"}
                        </Text>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Show all button when not searching */}
                {!searchQuery && isMenuOpen && !isLoadingSearch && (
                  <Box
                    position="absolute"
                    width="100%"
                    zIndex={1000}
                    bg="white"
                    boxShadow="lg"
                    borderRadius="lg"
                    mt={2}
                    border="1px"
                    borderColor="gray.200"
                    maxH="300px"
                    overflowY="auto"
                  >
                    <Box p={2} textAlign="center">
                      <Text fontSize="xs" color="gray.500" mb={2}>
                        Type to search or view available agents
                      </Text>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Assigned Agents Section */}
              {currentlyAssigned.length > 0 && (
                <Box
                  bg="white"
                  borderRadius="lg"
                  p={4}
                  border="1px"
                  borderColor="gray.200"
                >
                  <HStack mb={4} justify="space-between">
                    <Text fontWeight="bold" color="gray.700">
                      Assigned Agents ({currentlyAssigned.length})
                    </Text>
                    <Badge colorScheme="purple" variant="subtle">
                      {currentlyAssigned.length}
                    </Badge>
                  </HStack>

                  <VStack spacing={3} align="stretch">
                    {currentlyAssigned.map((agent) => {
                      const { fullName, phone } = getAgentDisplay(agent);
                      return (
                        <HStack
                          key={agent._id}
                          justify="space-between"
                          p={3}
                          bg="gray.50"
                          borderRadius="md"
                          border="1px"
                          borderColor="gray.200"
                          transition="all 0.2s"
                          _hover={{
                            bg: "gray.100",
                            borderColor: "gray.300",
                          }}
                        >
                          <VStack align="start" spacing={0} flex={1}>
                            <Text
                              fontWeight="600"
                              fontSize="sm"
                              color="gray.800"
                            >
                              {fullName}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {phone}
                            </Text>
                          </VStack>
                          <Button
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            leftIcon={<CloseIcon />}
                            onClick={() =>
                              toggleAgentSelection(agent._id?.toString())
                            }
                            fontSize="xs"
                          >
                            Remove
                          </Button>
                        </HStack>
                      );
                    })}
                  </VStack>
                </Box>
              )}

              {/* Empty State */}
              {currentlyAssigned.length === 0 && (
                <Box
                  textAlign="center"
                  py={8}
                  px={4}
                  bg="blue.50"
                  borderRadius="lg"
                  border="1px dashed"
                  borderColor="blue.200"
                >
                  <Text color="blue.600" fontWeight="500" mb={2}>
                    No agents assigned yet
                  </Text>
                  <Text color="blue.500" fontSize="sm">
                    Search and select agents above to assign them to this
                    project
                  </Text>
                </Box>
              )}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter
          bg="white"
          borderTopColor="gray.200"
          borderTopWidth="1px"
          pt={4}
          pb={4}
          px={{ base: 4, md: 6 }}
          gap={3}
          flexWrap={{ base: "wrap", md: "nowrap" }}
        >
          <HStack
            flex={1}
            justify="space-between"
            width={{ base: "100%", md: "auto" }}
            spacing={3}
          >
            <Text
              fontSize={{ base: "xs", md: "sm" }}
              fontWeight="600"
              color="gray.700"
              whiteSpace="nowrap"
            >
              Selected:{" "}
              <span style={{ color: "#667eea" }}>
                {currentlyAssigned.length}
              </span>{" "}
              agent
              {currentlyAssigned.length !== 1 ? "s" : ""}
            </Text>

            <HStack spacing={2} ml="auto">
              <Button
                onClick={onClose}
                variant="ghost"
                colorScheme="gray"
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                color="white"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                loadingText="Assigning..."
                fontSize={{ base: "sm", md: "md" }}
                px={{ base: 4, md: 6 }}
                _hover={{
                  opacity: 0.9,
                  transform: "translateY(-2px)",
                  boxShadow: "lg",
                }}
                transition="all 0.2s"
              >
                Assign Agents
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddAgentsToProjectModal;
